import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, Loader2, TrendingUp, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const STRUCTURES = [
  { v: "sole_prop", l: "Sole Proprietorship" },
  { v: "partnership", l: "Partnership" },
  { v: "single_llc", l: "Single-Member LLC" },
  { v: "multi_llc", l: "Multi-Member LLC" },
];

const money = (n) => `$${Number(n).toLocaleString()}`;

export default function CalculatorSection({ onChoosePackage }) {
  const [states, setStates] = useState([]);
  const [form, setForm] = useState({
    state: "TX", structure: "single_llc", gross_revenue: "", net_profit: "",
    num_owners: "1", has_payroll: "no", current_salary: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/states").then(({ data }) => setStates(data.states)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calculate = async () => {
    setError("");
    if (!form.net_profit || Number(form.net_profit) <= 0) {
      setError("Please enter your annual net profit.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/calculator", {
        state: form.state,
        structure: form.structure,
        gross_revenue: Number(form.gross_revenue) || 0,
        net_profit: Number(form.net_profit),
        num_owners: Number(form.num_owners) || 1,
        has_payroll: form.has_payroll === "yes",
        current_salary: Number(form.current_salary) || 0,
      });
      setResult(data);
      setTimeout(() => document.getElementById("calc-results")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch {
      setError("Could not calculate right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="calculator" className="py-20 lg:py-28 bg-white border-y border-black/5" data-testid="calculator-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] bg-[#E6F0EB] px-4 py-2 rounded-full">
            <Calculator className="h-3.5 w-3.5" /> Free Savings Calculator
          </span>
          <h2 className="mt-5 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
            See if an S-Corp makes sense for your business
          </h2>
          <p className="mt-3 text-[#595D5A]">
            Get a personalized estimate and a service recommendation in about 60 seconds.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="bg-[#F9F9F8] border border-[#E5E5E5] rounded-2xl p-6 lg:p-8" data-testid="calc-inputs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#1C1C1C]">Business state</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white" data-testid="calc-state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {states.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Current structure</Label>
                <Select value={form.structure} onValueChange={(v) => set("structure", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white" data-testid="calc-structure">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STRUCTURES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Annual gross revenue</Label>
                <Input type="number" value={form.gross_revenue} onChange={(e) => set("gross_revenue", e.target.value)}
                  data-testid="calc-gross" className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white py-6" placeholder="$250,000" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Annual net profit *</Label>
                <Input type="number" value={form.net_profit} onChange={(e) => set("net_profit", e.target.value)}
                  data-testid="calc-net" className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white py-6" placeholder="$120,000" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Number of owners</Label>
                <Input type="number" min="1" value={form.num_owners} onChange={(e) => set("num_owners", e.target.value)}
                  data-testid="calc-owners" className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white py-6" />
              </div>
              <div>
                <Label className="text-[#1C1C1C]">Currently run payroll?</Label>
                <Select value={form.has_payroll} onValueChange={(v) => set("has_payroll", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white" data-testid="calc-payroll">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.has_payroll === "yes" && (
                <div className="sm:col-span-2">
                  <Label className="text-[#1C1C1C]">Current owner salary</Label>
                  <Input type="number" value={form.current_salary} onChange={(e) => set("current_salary", e.target.value)}
                    data-testid="calc-salary" className="mt-1.5 rounded-xl border-[#E5E5E5] bg-white py-6" placeholder="$60,000" />
                </div>
              )}
            </div>
            {error && <p className="mt-4 text-sm text-[#EF4444]" data-testid="calc-error">{error}</p>}
            <Button onClick={calculate} disabled={loading} data-testid="calc-submit-btn"
              className="mt-6 w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white py-6 text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Calculate My Savings <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>

          {/* Results */}
          <div id="calc-results" className="rounded-2xl p-6 lg:p-8 bg-[#114A31] text-white flex flex-col" data-testid="calc-results">
            {!result ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-white/70">
                <TrendingUp className="h-10 w-10 mb-3 text-[#7FE3AE]" />
                <p>Enter your details and we'll estimate your potential annual savings.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-white/70">Estimated annual tax savings</p>
                <p className="font-heading text-5xl font-bold text-[#7FE3AE]" data-testid="calc-savings">
                  {money(result.estimated_annual_savings)}
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/60 text-xs">SE-tax reduction</p>
                    <p className="font-heading text-lg font-semibold">{money(result.se_tax_reduction)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/60 text-xs">Reasonable salary range</p>
                    <p className="font-heading text-lg font-semibold">{money(result.reasonable_salary_low)}–{money(result.reasonable_salary_high)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/60 text-xs">Break-even net profit</p>
                    <p className="font-heading text-lg font-semibold">{money(result.break_even_net_profit)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-white/60 text-xs">S-Corp recommended?</p>
                    <p className="font-heading text-lg font-semibold flex items-center gap-1">
                      {result.recommended ? <><CheckCircle2 className="h-4 w-4 text-[#7FE3AE]" /> Yes</> : "Not yet"}
                    </p>
                  </div>
                </div>

                <div className="mt-5 bg-white/10 rounded-xl p-4">
                  <p className="text-sm text-white/90">{result.recommendation_text}</p>
                </div>

                {result.state_note && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-[#FEF3C7]">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {result.state_note}
                  </div>
                )}

                <div className="mt-auto pt-6">
                  <p className="text-sm text-white/70 mb-2">Recommended plan: <span className="font-semibold text-white">{result.recommended_package_name}</span></p>
                  <Button onClick={() => onChoosePackage?.(result.recommended_package)} data-testid="calc-package-btn"
                    className="w-full rounded-full bg-white text-[#114A31] hover:bg-[#E6F0EB] py-6">
                    View {result.recommended_package_name} plan <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="mt-3 text-[11px] text-white/50">{result.assumptions[result.assumptions.length - 1]}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
